/**
 * Market Context — VWAP, structure, regime, opening range, ATR (§5, §16, §17, §19)
 *
 * Everything here is a pure function of candles[0..i] with i a CLOSED bar,
 * so values used for signals never repaint (§28). VWAP is session-anchored
 * (cumulative from the session's first candle).
 */

import { CandleData } from "../../../types/chart.types";
import { MarketRegime, StructureState, SessionAgg } from "./types";

// ─── ATR ──────────────────────────────────────────────────────────

/** Average True Range at closed bar `idx` (period bars ending at idx). */
export const atrAt = (candles: CandleData[], idx: number, period = 14): number => {
  if (idx < 0) return 0;
  const start = Math.max(1, idx - period + 1);
  let sum = 0;
  let n = 0;
  for (let i = start; i <= idx; i++) {
    const h = candles[i].high;
    const l = candles[i].low;
    const pc = candles[i - 1].close;
    sum += Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc));
    n++;
  }
  return n > 0 ? sum / n : Math.abs(candles[idx].high - candles[idx].low);
};

// ─── Session VWAP (§16) ───────────────────────────────────────────

/**
 * Session-anchored VWAP for every bar of `session`.
 * Returns a sparse map indexed by candle index:
 *   vwap[i] = Σ(typical*vol)/Σ(vol) over session bars up to i.
 * Falls back to cumulative typical-price average when total volume is 0.
 */
export const buildSessionVwap = (
  candles: CandleData[],
  session: SessionAgg,
): Map<number, number> => {
  const out = new Map<number, number>();
  let sumPV = 0;
  let sumV = 0;
  let sumTP = 0;
  let n = 0;
  for (let i = session.firstIdx; i <= session.lastIdx; i++) {
    const c = candles[i];
    const tp = (c.high + c.low + c.close) / 3;
    const v = c.volume || 0;
    sumPV += tp * v;
    sumV += v;
    sumTP += tp;
    n++;
    out.set(i, sumV > 0 ? sumPV / sumV : sumTP / n);
  }
  return out;
};

// ─── Market structure (§19/§20) ───────────────────────────────────

/**
 * Simple two-half swing structure over the last `lookback` closed bars:
 * BULLISH when the recent half makes both a higher high and a higher low,
 * BEARISH when it makes both lower, else NEUTRAL.
 */
export const marketStructure = (
  candles: CandleData[],
  lastClosedIdx: number,
  lookback = 20,
): StructureState => {
  if (lastClosedIdx < 4) return "NEUTRAL";
  const len = Math.min(lookback, lastClosedIdx + 1);
  const start = lastClosedIdx - len + 1;
  const mid = start + Math.floor(len / 2);
  if (mid <= start || lastClosedIdx < mid) return "NEUTRAL";

  let h1 = -Infinity, l1 = Infinity, h2 = -Infinity, l2 = Infinity;
  for (let i = start; i < mid; i++) {
    if (candles[i].high > h1) h1 = candles[i].high;
    if (candles[i].low < l1) l1 = candles[i].low;
  }
  for (let i = mid; i <= lastClosedIdx; i++) {
    if (candles[i].high > h2) h2 = candles[i].high;
    if (candles[i].low < l2) l2 = candles[i].low;
  }

  if (h2 > h1 && l2 > l1) return "BULLISH";
  if (h2 < h1 && l2 < l1) return "BEARISH";
  return "NEUTRAL";
};

// ─── Market regime (§17) ──────────────────────────────────────────

/**
 * Deterministic regime classifier over closed bars:
 *   TRANSITION — the last closed bar crossed a CPR boundary
 *   EXPANSION  — mean range of the last 5 bars ≥ 1.3× the last-20 mean
 *   TRENDING   — Kaufman efficiency ratio ≥ 0.40
 *   CHOPPY     — efficiency ratio ≤ 0.15
 *   RANGING    — everything else
 */
export const marketRegime = (
  candles: CandleData[],
  lastClosedIdx: number,
  cprTop: number,
  cprBottom: number,
  lookback = 20,
): MarketRegime => {
  if (lastClosedIdx < 2) return "RANGING";

  // Boundary transition check
  const prev = candles[lastClosedIdx - 1].close;
  const cur = candles[lastClosedIdx].close;
  const crossedUp = (prev <= cprTop && cur > cprTop) || (prev <= cprBottom && cur > cprBottom);
  const crossedDown = (prev >= cprTop && cur < cprTop) || (prev >= cprBottom && cur < cprBottom);
  if (crossedUp || crossedDown) return "TRANSITION";

  const len = Math.min(lookback, lastClosedIdx + 1);
  const start = lastClosedIdx - len + 1;

  // Kaufman efficiency ratio
  let path = 0;
  for (let i = start + 1; i <= lastClosedIdx; i++) {
    path += Math.abs(candles[i].close - candles[i - 1].close);
  }
  const net = Math.abs(candles[lastClosedIdx].close - candles[start].close);
  const er = path > 0 ? net / path : 0;

  // Range expansion ratio (last 5 vs last 20)
  let sum5 = 0, n5 = 0, sum20 = 0, n20 = 0;
  for (let i = start; i <= lastClosedIdx; i++) {
    const r = candles[i].high - candles[i].low;
    sum20 += r;
    n20++;
    if (i >= lastClosedIdx - 4) { sum5 += r; n5++; }
  }
  const avg20 = n20 > 0 ? sum20 / n20 : 0;
  const avg5 = n5 > 0 ? sum5 / n5 : 0;
  const atrRatio = avg20 > 0 ? avg5 / avg20 : 1;

  if (atrRatio >= 1.3) return "EXPANSION";
  if (er >= 0.4) return "TRENDING";
  if (er <= 0.15) return "CHOPPY";
  return "RANGING";
};

// ─── Opening range (§5, §11) ──────────────────────────────────────

/** High/low of the first `orMinutes` of the session (first candle if none). */
export const openingRange = (
  candles: CandleData[],
  session: SessionAgg,
  orMinutes: number,
): { high: number; low: number } => {
  const deadline = session.start + Math.max(1, orMinutes) * 60;
  let high = -Infinity;
  let low = Infinity;
  let n = 0;
  for (let i = session.firstIdx; i <= session.lastIdx; i++) {
    if (candles[i].time >= deadline && n > 0) break;
    if (candles[i].high > high) high = candles[i].high;
    if (candles[i].low < low) low = candles[i].low;
    n++;
  }
  if (n === 0) {
    const c = candles[session.firstIdx];
    return { high: c.high, low: c.low };
  }
  return { high, low };
};

/**
 * Count of times price crossed VWAP or either CPR boundary over the
 * last `window` closed bars — used for the "LOW CONVICTION / CHOPPY"
 * classification (§16).
 */
export const countChopCrosses = (
  candles: CandleData[],
  lastClosedIdx: number,
  vwap: Map<number, number>,
  cprTop: number,
  cprBottom: number,
  window = 10,
): number => {
  const start = Math.max(1, lastClosedIdx - window + 1);
  let crosses = 0;
  let prevRelV: number | null = null;
  let prevRelTop: number | null = null;
  let prevRelBot: number | null = null;
  for (let i = start; i <= lastClosedIdx; i++) {
    const c = candles[i].close;
    const v = vwap.get(i);
    if (v != null) {
      const rel = c >= v ? 1 : -1;
      if (prevRelV != null && rel !== prevRelV) crosses++;
      prevRelV = rel;
    }
    const t = c >= cprTop ? 1 : -1;
    if (prevRelTop != null && t !== prevRelTop) crosses++;
    prevRelTop = t;
    const b = c >= cprBottom ? 1 : -1;
    if (prevRelBot != null && b !== prevRelBot) crosses++;
    prevRelBot = b;
  }
  return crosses;
};
